import { useEffect, useRef, useState } from 'react'
import type { LiveSession, LeaderboardState, LeaderboardEntry, TeamStanding } from '@gymboard/types'
import { formatTeamScores } from '../components/leaderboard/teamScore'
import CurtainStage, { minutes } from '../components/spotlight/CurtainStage'
import ChorusList from '../components/spotlight/ChorusList'
import { useHandoff } from '../components/spotlight/useHandoff'
import '../components/spotlight/spotlight.css'
import { getTenantInfo } from '../lib/storage'

interface Props {
  session: LiveSession | null
  leaderboard: LeaderboardState | null
  onDismiss: () => void
}

/** Back to the ambient screen after this long. */
const AUTO_DISMISS_SECS = 75
/** Each podium place holds the stage this long before the next bow. */
const BOW_MS = 3400

const REDUCE_MOTION = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = (s % 60).toString().padStart(2, '0')
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

const redMs = (e: LeaderboardEntry) => (e.zoneBreakdown ? e.zoneBreakdown.zone4Ms + e.zoneBreakdown.zone5Ms : 0)

/**
 * Honours beyond the podium, so more of the room hears its name. Each one
 * goes to the top athlete on that measure; ties go to the higher rank.
 */
function honours(entries: LeaderboardEntry[]) {
  const best = (score: (e: LeaderboardEntry) => number) =>
    entries.reduce<LeaderboardEntry | null>((b, e) => (score(e) > (b ? score(b) : 0) ? e : b), null)
  const red = best(redMs)
  const kcal = best((e) => e.calories ?? 0)
  const peak = best((e) => e.peakHeartRate ?? 0)
  return [
    red && redMs(red) >= 60_000 && { key: 'red', icon: ICON_FLAME, label: 'Most time in zones 4–5', who: red.athleteName, value: minutes(redMs(red)) },
    kcal && kcal.calories > 0 && { key: 'kcal', icon: ICON_BURN, label: 'Most calories', who: kcal.athleteName, value: `${Math.round(kcal.calories).toLocaleString()} kcal` },
    peak && peak.peakHeartRate && { key: 'peak', icon: ICON_PEAK, label: 'Highest heart rate', who: peak.athleteName, value: `${Math.round(peak.peakHeartRate)} BPM` },
  ].filter(Boolean) as { key: string; icon: JSX.Element; label: string; who: string; value: string }[]
}

function TeamResult({ teams }: { teams: TeamStanding[] }) {
  const ranked = [...teams].sort((a, b) => b.score - a.score)
  const fmt = formatTeamScores(ranked)
  const top = ranked[0]?.score ?? 0
  const winners = ranked.filter((t) => t.score === top)
  const tie = winners.length > 1
  return (
    <div className="cc-teams">
      <p>{tie ? `Tie: ${winners.map((t) => `Team ${t.name}`).join(' and ')}` : `Team ${winners[0].name} wins`}</p>
      {ranked.map((t) => (
        <span key={t.team} className="cc-team">
          <i style={{ background: t.color }}>{t.name[0]}</i>
          {t.name}
          <b>{fmt(t.score)}</b>
          {!tie && t.team === winners[0].team && <em style={{ background: t.color }}>Won</em>}
        </span>
      ))}
    </div>
  )
}

export default function EndedScreen({ session, leaderboard, onDismiss }: Props) {
  const tenantInfo = getTenantInfo()
  const entries = leaderboard?.entries ?? []
  const scoringMode = leaderboard?.scoringMode ?? session?.scoringMode ?? 'EFFORT_SCORE'
  const unit = scoringMode === 'CALORIES_ONLY' ? 'kcal' : 'pts'

  // Curtain call: third, then second, then the winner, who keeps the stage.
  const podium = entries.slice(0, 3)
  const order = [...podium].reverse()
  const [step, setStep] = useState(REDUCE_MOTION ? order.length - 1 : 0)
  useEffect(() => {
    if (step >= order.length - 1) return
    const t = setTimeout(() => setStep((s) => s + 1), BOW_MS)
    return () => clearTimeout(t)
  }, [step, order.length])

  const [left, setLeft] = useState(AUTO_DISMISS_SECS)
  useEffect(() => {
    const id = setInterval(() => setLeft((s) => s - 1), 1000)
    return () => clearInterval(id)
  }, [])
  useEffect(() => {
    if (left <= 0) onDismiss()
  }, [left, onDismiss])

  const hero = order[Math.max(0, Math.min(step, order.length - 1))] ?? null
  const swapKey = hero ? `bow:${hero.athleteId}` : 'none'
  const rootRef = useRef<HTMLDivElement>(null)
  const nameRef = useRef<HTMLDivElement>(null)
  useHandoff(swapKey, hero?.athleteId ?? null, rootRef, nameRef)

  const className = session?.classInstance?.template?.name ?? 'Class'
  const coach = session?.classInstance?.coach as { firstName?: string; lastName?: string } | undefined
  const coachName = coach?.firstName ? `${coach.firstName} ${coach.lastName ?? ''}`.trim() : null
  const roomName = (session?.classInstance as { room?: { name?: string } } | undefined)?.room?.name ?? null
  const classMs =
    session?.startedAt && session?.endedAt
      ? new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime() - (session.totalPausedMs ?? 0)
      : 0
  const kcal = Math.round(entries.reduce((s, e) => s + (e.calories ?? 0), 0))
  const awards = honours(entries)

  return (
    <div ref={rootRef} className="w-screen h-screen relative bg-[#0E1022] overflow-hidden flex flex-col">
      <header className="sp-head">
        <svg className="mark" viewBox="0 0 64 64" aria-hidden="true">
          <g transform="translate(4 4)">
            <path d="M14.9 45.2A24 24 0 1 1 41.1 45.2" fill="none" stroke="#F1EEE8" strokeWidth="5.5" strokeLinecap="round" />
            <path d="M28 12c5 7.5 11 12 11 20.5A11 11 0 0 1 17 32.5c0-5 2.6-8.4 5.3-11.3 1 3.3 2.8 5.3 5.2 6.3C28.4 21.8 27.5 16.8 28 12Z" fill="#C93A30" />
          </g>
        </svg>
        <span className="sp-status" style={{ color: '#F1EEE8', background: 'rgba(241, 238, 232, 0.1)' }}>
          {ICON_CHECK}
          Class complete
        </span>
        <div className="sp-class">
          <h1>{className}</h1>
          <p>{[coachName, roomName].filter(Boolean).join('  /  ') || ' '}</p>
        </div>
        {classMs > 0 && (
          <div className="sp-clock">
            <b>{clock(classMs)}</b>
            <span>Class time</span>
          </div>
        )}
        <div className="sp-meta">
          <b>{entries.length} {entries.length === 1 ? 'athlete' : 'athletes'}</b>
          {kcal > 0 && <span>{kcal.toLocaleString()} kcal together</span>}
        </div>
        <div className="sp-meta">
          <b>{tenantInfo?.name ?? 'Agaru'}</b>
        </div>
      </header>

      {leaderboard?.teams && leaderboard.teams.length > 0 && <TeamResult teams={leaderboard.teams} />}

      <div className="sp-main">
        {hero ? (
          <CurtainStage ref={nameRef} hero={hero} total={entries.length} unit={unit} teams={leaderboard?.teams} swapKey={swapKey} />
        ) : (
          <div className="sp-stage">
            <div className="sp-empty">
              <p>Class complete</p>
              <small>No heart-rate data came through this class.</small>
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-col">
          {entries.length > 0 && <ChorusList entries={entries} spotlightId={hero?.athleteId ?? null} final />}
        </div>
      </div>

      <footer className="cc-foot">
        {awards.map((a) => (
          <div key={a.key} className="cc-honour">
            <span className="cc-ic">{a.icon}</span>
            <div>
              <small>{a.label}</small>
              <p>
                <b>{a.who}</b> {a.value}
              </p>
            </div>
          </div>
        ))}
        <span className="cc-next">Next screen in {Math.max(0, left)}s</span>
      </footer>
    </div>
  )
}

const ICON_CHECK = (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const ICON_FLAME = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2.5c3 4.5 6.5 7.2 6.5 12.2a6.5 6.5 0 0 1-13 0c0-3 1.5-5 3.1-6.7.6 2 1.7 3.2 3.1 3.8.6-3.4 0-6.4.3-9.3Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
)
const ICON_BURN = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12L13 2Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
)
const ICON_PEAK = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2.5 12.5h4l2.5-6 4 12 3-8 1.5 2h4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
